import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { withErrorHandling } from '@selfserve/react-hocs';
import {
  StyledContentContainer,
  CommonLayout,
  StyledTitle,
} from '@selfserve/react-ui';
import {
  apiStatuses,
  getRecaptchaToken,
  routesPaths,
} from '@selfserve/react-utils';
import { SetPasswordForm } from '../ui/set-password-form';
import { useSetPasswordRequest } from '../data';

const StyledSetPasswordContainer = styled.div`
  max-width: 448px;
  width: 100%;
`;

const StyledSetPasswordTitle = styled(StyledTitle)`
  display: block;
  margin-bottom: 32px;
  text-align: center;
`;

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    password: Yup.mixed().required(t('common.validations.password-required')),
    confirmPassword: Yup.string().when('password', {
      is: (password: string) => !!password,
      then: Yup.string()
        .required(t('common.validations.confirm-password-required'))
        .oneOf(
          [Yup.ref('password')],
          t('common.validations.confirm-password-match')
        ),
    }),
    passwordErrors: Yup.object().when('password', (password) => {
      return Yup.object().shape({
        minLengthErr: Yup.mixed().test({
          test: () => !!(password && password.length >= 8),
        }),
        oneNumberErr: Yup.mixed().test({
          test: () => !!(password && /\d/.test(`${password}`)),
        }),
        upperCaseErr: Yup.mixed().test({
          test: () => !!(password && `${password}`.match('.*[A-Z].*')),
        }),
        lowerCaseErr: Yup.mixed().test({
          test: () => !!(password && `${password}`.match('.*[a-z].*')),
        }),
      });
    }),
  });

export interface FormValues {
  password: string;
  passwordErrors: {
    minLengthErr: boolean;
    oneNumberErr: boolean;
    upperCaseErr: boolean;
    lowerCaseErr: boolean;
  };
  confirmPassword: string;
}

export const SetPassword = withErrorHandling(({ openErrorModal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isInProccess, setIsInProccess] = useState(false);
  const { token: passwordHash } = useParams();
  const {
    data: setPasswordResponse,
    mutateAsync: handleSetPasswordRequest,
    isLoading,
  } = useSetPasswordRequest();
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      password: '',
      confirmPassword: '',
      passwordErrors: {
        minLengthErr: false,
        oneNumberErr: false,
        upperCaseErr: false,
        lowerCaseErr: false,
      },
    },
    validationSchema: validationSchema(t),
    onSubmit: ({ password: newPassword }) => {
      setIsInProccess(true);
      getRecaptchaToken()
        .then((recaptchaToken: string) => {
          handleSetPasswordRequest({
            recaptchaToken,
            passwordHash,
            newPassword,
          });
        })
        .catch(() => setIsInProccess(false));
    },
  });
  const { values, touched, setFieldTouched } = formik;

  useEffect(() => {
    const { data, status } = setPasswordResponse || {};
    if (status) {
      setIsInProccess(false);
      if (status === apiStatuses.BAD_REQUEST) {
        openErrorModal({
          text: data?.message_en,
          title: t('common.cant-reset-password'),
        });
      }
      if (status === apiStatuses.CREATED) {
        navigate(routesPaths.HOMEPAGE);
      }
    }
  }, [setPasswordResponse]);

  useEffect(() => {
    if (values.password && !touched.password) {
      setFieldTouched('password', true);
    }
  }, [values.password]);

  return (
    <CommonLayout showSideImage>
      <StyledContentContainer centered>
        <StyledSetPasswordContainer>
          <StyledSetPasswordTitle>
            {t('common.set-password')}
          </StyledSetPasswordTitle>
          <SetPasswordForm
            formik={formik}
            isLoading={isLoading || isInProccess}
          />
        </StyledSetPasswordContainer>
      </StyledContentContainer>
    </CommonLayout>
  );
});
